/* Single-application summary page (read-only).
   Route: financeApp:<id>
   Sections: header · officer decision · gold collateral · guarantor
   cheques · repayment cheques · disbursement payment (A+B) · documents
   · activity timeline. Admin can Edit (re-open wizard) or Reopen (flip
   finalised back to draft). Print uses native browser print with a
   small @media-print stylesheet override. */

const { useState: useStateFA, useEffect: useEffectFA } = React;

function FinanceApplicationPage({ appId, go }) {
  const [app, setApp] = useStateFA(null);
  const [timeline, setTimeline] = useStateFA([]);
  const [busy, setBusy] = useStateFA(true);
  const [err, setErr] = useStateFA('');
  const [info, setInfo] = useStateFA('');
  // 'overview' shows the existing card stack; 'statement' shows the
  // numbered installment ledger. Default is overview to preserve the
  // existing landing behaviour.
  const [tab, setTab] = useStateFA('overview');
  // Either { kind: 'repayment', row } or { kind: 'guarantor', row } when
  // a clearance modal is open. Null otherwise.
  const [clearing, setClearing] = useStateFA(null);

  const reload = async () => {
    setBusy(true); setErr('');
    try {
      const [a, t] = await Promise.all([
        window.QH_API.get(`/finance/applications/${appId}`),
        window.QH_API.get(`/finance/applications/${appId}/timeline`).catch(() => []),
      ]);
      setApp(a);
      setTimeline(t || []);
    } catch (e) { setErr(e.message || 'Failed to load application.'); }
    finally { setBusy(false); }
  };

  useEffectFA(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [appId]);

  const reopen = async () => {
    if (!confirm('Reopen this application as a draft? It returns to the wizard for edits.')) return;
    try {
      await window.QH_API.post(`/finance/applications/${appId}/reopen-draft`, {});
      await reload();
      setInfo('Application reopened as draft.');
    } catch (e) { setErr(e.message || 'Reopen failed.'); }
  };

  if (busy && !app) return <div className="page-body"><div style={{padding:24,color:'var(--ink-3)'}}>Loading…</div></div>;
  if (err && !app) return <div className="page-body"><div style={{padding:24,color:'var(--neg)'}}>{err}</div></div>;
  if (!app) return null;

  const isAdmin = QH_DATA.activeRole?.id === 'admin';
  const canEdit = window.can?.('finance_apps', 'edit');
  const isFinalised = app.status === 'approved' || app.status === 'rejected';
  const fmtBHD = window.fmtBHD || ((n) => Number(n || 0).toFixed(3));
  const fmtDate = (d) => (d || '').slice(0, 10) || '—';

  const statusBadge = (() => {
    const s = app.status;
    const cls = s === 'approved' ? 'pos' : s === 'rejected' ? 'neg' : s === 'draft' ? '' : 'warn';
    return <span className={`tag ${cls}`} style={{textTransform:'uppercase'}}>{s}</span>;
  })();

  const goldItems = app.goldItems || [];
  const totalGoldWeight = goldItems.reduce((s, g) => s + Number(g.weightGrams || 0), 0);
  const totalGoldValue = goldItems.reduce((s, g) => s + Number(g.estimatedValue || 0), 0);
  const cheques = app.guarantorCheques || [];
  const reps = app.repayments || [];
  const payouts = app.disbursementPayouts || [];
  const rotations = app.disbursementRotations || [];
  const payoutTotal = payouts.reduce((s, p) => s + Number(p.amount || 0), 0);
  const rotationTotal = rotations.reduce((s, r) => s + Number(r.amount || 0), 0);
  const paymentTotal = payoutTotal + rotationTotal;

  return (
    <>
      <div className="page-header no-print">
        <div>
          <div className="breadcrumb">
            <span className="clickable" onClick={() => go('finance')} style={{cursor:'pointer'}}>Finance Apps</span>
            <I.ChevronRight s={10}/>
            <span>{app.id}</span>
          </div>
          <h1>{app.applicant} <span className="font-mono muted text-xs" style={{marginLeft:8}}>{app.id}</span> {statusBadge}</h1>
          <div className="subtitle">
            Applied {fmtDate(app.applied)}{app.officerName ? ` · Officer ${app.officerName}` : ''}
          </div>
        </div>
        <div className="page-header-actions">
          {canEdit && !isFinalised && (
            <button className="btn" onClick={() => go(`financeWizard:${app.id}`)}><I.Edit/> Resume in wizard</button>
          )}
          {canEdit && isFinalised && isAdmin && (
            <button className="btn" onClick={reopen}><I.Refresh/> Reopen as draft</button>
          )}
          <button className="btn ghost" onClick={() => window.print()}><I.Download/> Print</button>
        </div>
      </div>

      {/* Print-only header — the on-screen page-header is hidden by the
          @media print rules. This gives the printed copy a clear title
          + filing-friendly metadata (applicant, ID, applied date) so
          the printout is self-describing without the app chrome. */}
      <div className="print-only" style={{display:'none', padding:'0 0 12px', borderBottom:'1px solid #999', marginBottom:12}}>
        <div style={{fontSize:20, fontWeight:700, color:'#000'}}>Finance Application — {app.applicant}</div>
        <div style={{fontSize:12, color:'#444', marginTop:4}}>
          ID: <span style={{fontFamily:'var(--font-mono)'}}>{app.id}</span>
          {' · '}Status: <span style={{textTransform:'uppercase'}}>{app.status}</span>
          {' · '}Applied: {fmtDate(app.applied)}
          {app.officerName ? ` · Officer: ${app.officerName}` : ''}
          {' · '}Printed: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Tabs sit ABOVE the page-body so the Summary card hugs the page
          header. The whole strip is hidden on print — printouts get
          everything (overview + statement) regardless of selected tab.
          See the {tab === '...' && } gates below. */}
      <div className="tabs no-print" style={{padding: '0 var(--pad)'}}>
        <button className={`tab ${tab === 'overview'  ? 'active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
        <button className={`tab ${tab === 'statement' ? 'active' : ''}`} onClick={() => setTab('statement')}>
          Statement <span className="count">{(app.repayments || []).length}</span>
        </button>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        {info && <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--pos)',borderRadius:6,fontSize:13}}>{info}</div>}
        {err && <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--neg)',borderRadius:6,fontSize:13}}>{err}</div>}

        {/* Statement view is a single table — installment number, due
            date, name on the cheque, amount, clearance state, comment.
            Driven entirely off app.repayments (already ordered by DueDate
            from the server). Rendered first when selected so the long
            scroll on the overview doesn't compete with it. */}
        {tab === 'statement' && (
          <StatementTab app={app} onClearCheque={(row) => setClearing({ kind: 'repayment', row })}/>
        )}

        {tab === 'overview' && <>
        {/* ── Header / KPIs ─────────────────────────────────── */}
        <div className="card">
          <div className="card-header">Summary</div>
          <div className="card-body" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,fontSize:13}}>
            <div><div className="muted text-xs uppercase">Applicant</div><div>{app.applicant}{app.memberItsNo && <> · <span className="font-mono">{app.memberItsNo}</span></>}</div></div>
            <div><div className="muted text-xs uppercase">Purpose</div><div>{window.formatPurpose ? window.formatPurpose(app.purpose, app.purposeOther) : (app.purpose || '—')}</div></div>
            <div><div className="muted text-xs uppercase">Applied</div><div className="font-mono">{fmtBHD(app.appliedAmount)} BHD</div></div>
            <div><div className="muted text-xs uppercase">Approved</div><div className="font-mono" style={{fontWeight:600}}>{fmtBHD(app.amount)} BHD</div></div>
            <div><div className="muted text-xs uppercase">Tenure</div><div>{app.tenure} months</div></div>
            <div><div className="muted text-xs uppercase">Applied on</div><div className="font-mono">{fmtDate(app.applied)}</div></div>
            <div><div className="muted text-xs uppercase">{app.status === 'rejected' ? 'Rejected on' : 'Approved on'}</div><div className="font-mono">{fmtDate(app.status === 'rejected' ? app.rejectedOn : app.approvedOn)}</div></div>
            <div><div className="muted text-xs uppercase">Officer</div><div>{app.officerName || '—'}</div></div>
          </div>
        </div>

        {app.reason && (
          <div className="card">
            <div className="card-header">{app.status === 'rejected' ? 'Rejection reason' : 'Officer notes'}</div>
            <div className="card-body" style={{whiteSpace:'pre-wrap'}}>{app.reason}</div>
          </div>
        )}
        {app.notes && (
          <div className="card">
            <div className="card-header">Applicant notes</div>
            <div className="card-body" style={{whiteSpace:'pre-wrap'}}>{app.notes}</div>
          </div>
        )}

        {/* ── Gold security ─────────────────────────────────── */}
        {app.status !== 'rejected' && (
          <div className="card">
            <div className="card-header">Gold security · {goldItems.length} item{goldItems.length === 1 ? '' : 's'}</div>
            <div className="card-body" style={{padding:0}}>
              {goldItems.length === 0 ? <div style={{padding:14}} className="text-xs muted">No gold collateral recorded.</div> : (
                <table className="data">
                  <thead><tr>
                    <th style={{width:90}}>Karat</th><th className="right" style={{width:120}}>Weight (g)</th>
                    <th>Description</th><th className="right" style={{width:140}}>Est. value (BHD)</th>
                  </tr></thead>
                  <tbody>
                    {goldItems.map(g => (
                      <tr key={g.id}>
                        <td className="font-mono">{g.karat}</td>
                        <td className="num right font-mono">{Number(g.weightGrams).toFixed(3)}</td>
                        <td>{g.description || '—'}</td>
                        <td className="num right font-mono">{g.estimatedValue != null ? Number(g.estimatedValue).toFixed(3) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'var(--bg-sunken)'}}>
                      <td className="muted text-xs">Total</td>
                      <td className="num right font-mono" style={{fontWeight:700}}>{totalGoldWeight.toFixed(3)}</td>
                      <td/>
                      <td className="num right font-mono" style={{fontWeight:700}}>{totalGoldValue.toFixed(3)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Guarantor cheques ─────────────────────────────── */}
        {app.status !== 'rejected' && (
          <div className="card">
            <div className="card-header">Guarantor cheques · {cheques.length}</div>
            <div className="card-body" style={{padding:0}}>
              {cheques.length === 0 ? <div style={{padding:14}} className="text-xs muted">No guarantor cheques.</div> : (
                <table className="data">
                  <thead><tr>
                    <th>Guarantor</th><th>Bank</th><th>Cheque #</th><th>Cheque date</th>
                    <th className="right">Amount (BHD)</th><th>Status</th><th>Cleared on</th><th className="no-print"/>
                  </tr></thead>
                  <tbody>
                    {cheques.map(c => {
                      const isCleared = c.status === 'cleared';
                      return (
                        <tr key={c.id}>
                          <td>{c.guarantorName}{c.guarantorItsNo && <span className="muted font-mono text-xs"> · {c.guarantorItsNo}</span>}</td>
                          <td>{c.bank || '—'}</td>
                          <td className="font-mono text-xs">{c.chequeNumber || '—'}</td>
                          <td className="font-mono text-xs">{fmtDate(c.chequeDate)}</td>
                          <td className="num right font-mono">{Number(c.amount).toFixed(3)}</td>
                          <td><span className={`tag ${isCleared ? 'pos' : c.status === 'bounced' ? 'neg' : ''}`} style={{textTransform:'uppercase'}}>{c.status}</span></td>
                          <td className="font-mono text-xs">{isCleared ? fmtDate(c.clearedOn) : '—'}</td>
                          <td className="no-print" style={{whiteSpace:'nowrap',textAlign:'right'}}>
                            {canEdit && !isCleared && (
                              <button className="btn sm" onClick={() => setClearing({ kind: 'guarantor', row: c })} title="Record cheque clearance">
                                <I.Check s={11}/> Clear
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Repayment cheques ─────────────────────────────── */}
        {app.status !== 'rejected' && (
          <div className="card">
            <div className="card-header">Repayment cheques · {reps.length}</div>
            <div className="card-body" style={{padding:0}}>
              {reps.length === 0 ? <div style={{padding:14}} className="text-xs muted">No repayment cheques.</div> : (
                <table className="data">
                  <thead><tr>
                    <th>Issuer</th><th>Bank</th><th>Cheque #</th><th>Due</th>
                    <th className="right">Amount (BHD)</th><th>Status</th><th>Cleared on</th><th>Notes</th><th className="no-print"/>
                  </tr></thead>
                  <tbody>
                    {reps.map(r => {
                      const isCleared = r.status === 'cleared';
                      const isRotated = !!r.rotatedToAppId;
                      return (
                        <tr key={r.id}>
                          <td>{r.issuerName || r.applicant || app.applicant}</td>
                          <td>{r.bank}</td>
                          <td className="font-mono text-xs">{r.cheque}</td>
                          <td className="font-mono text-xs">{fmtDate(r.dueDate)}</td>
                          <td className="num right font-mono">{Number(r.amount).toFixed(3)}</td>
                          <td><span className={`tag ${isCleared ? 'pos' : r.status === 'bounced' ? 'neg' : 'warn'}`} style={{textTransform:'uppercase'}}>{r.status}</span></td>
                          <td className="font-mono text-xs">{fmtDate(r.clearedOn)}</td>
                          <td className="text-xs muted">{isRotated ? <>Rotated to <span className="font-mono">{r.rotatedToAppId}</span></> : (r.clearanceComment || '')}</td>
                          <td className="no-print" style={{whiteSpace:'nowrap',textAlign:'right'}}>
                            {canEdit && !isCleared && r.status !== 'bounced' && !isRotated && (
                              <button className="btn sm" onClick={() => setClearing({ kind: 'repayment', row: r })} title="Record cheque clearance">
                                <I.Check s={11}/> Clear
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Disbursement payment ──────────────────────────── */}
        {app.status !== 'rejected' && (
          <div className="card">
            <div className="card-header">
              Disbursement payment · A + B = approved
              <span className="actions"><span className="muted text-xs">A bank {payoutTotal.toFixed(3)} + B rotated {rotationTotal.toFixed(3)} = <b>{paymentTotal.toFixed(3)} BHD</b></span></span>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:10}}>
              <div>
                <div className="text-xs uppercase muted" style={{marginBottom:4}}>Section A · Bank payouts</div>
                {payouts.length === 0 ? <div className="text-xs muted">No bank payouts.</div> : (
                  <table className="data">
                    <thead><tr>
                      <th>Bank</th><th>Cheque / ref</th><th>Cheque date</th><th>Description</th>
                      <th className="right">Amount (BHD)</th><th>Status</th><th>Cleared on</th><th className="no-print"></th>
                    </tr></thead>
                    <tbody>
                      {payouts.map(p => (
                        <PayoutRow key={p.id} payout={p} canEdit={canEdit} onChanged={reload}/>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <div className="text-xs uppercase muted" style={{marginBottom:4}}>Section B · Rotated repayment cheques</div>
                {rotations.length === 0 ? <div className="text-xs muted">No rotated cheques.</div> : (
                  <table className="data">
                    <thead><tr>
                      <th>Source applicant</th><th>Bank</th><th>Cheque #</th><th>Due</th><th>Rotated on</th>
                      <th>Source cleared</th><th className="right">Amount (BHD)</th>
                    </tr></thead>
                    <tbody>
                      {rotations.map(r => (
                        <tr key={r.id}>
                          <td>{r.sourceApplicant}</td>
                          <td>{r.sourceBank}</td>
                          <td className="font-mono text-xs">{r.sourceCheque}</td>
                          <td className="font-mono text-xs">{fmtDate(r.sourceDueDate)}</td>
                          <td className="font-mono text-xs">{fmtDate(r.rotatedOn)}</td>
                          <td className="font-mono text-xs">{fmtDate(r.sourceClearedOn)}</td>
                          <td className="num right font-mono">{Number(r.amount).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Documents ─────────────────────────────────────── */}
        <window.DocumentAttachments
          entityType="finance_app"
          entityId={app.id}
          editable={canEdit}
          title="Attached documents"
          emptyMsg="No documents attached."/>

        {/* ── Activity timeline ─────────────────────────────── */}
        <div className="card">
          <div className="card-header">Activity · {timeline.length}</div>
          <div className="card-body" style={{padding:0}}>
            {timeline.length === 0 ? <div style={{padding:14}} className="text-xs muted">No audit log entries yet.</div> : (
              <table className="data">
                <thead><tr>
                  <th>When</th><th>Who</th><th>Action</th><th>Detail</th>
                </tr></thead>
                <tbody>
                  {timeline.map((a, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">{(a.timestamp || '').replace('T',' ').slice(0,16)}</td>
                      <td className="text-xs">{a.userName} <span className="muted">· {a.role}</span></td>
                      <td><span className="tag" style={{textTransform:'uppercase'}}>{a.action}</span></td>
                      <td className="text-xs muted">{a.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        </>}
      </div>

      {clearing && (
        <window.ClearChequeModal
          title={clearing.kind === 'guarantor' ? 'Mark guarantor cheque cleared' : 'Mark cheque cleared'}
          subtitle={clearing.kind === 'guarantor'
            ? `Guarantor ${clearing.row.guarantorName} · cheque ${clearing.row.chequeNumber || '—'}`
            : `Repayment ${clearing.row.cheque} · ${clearing.row.issuerName || clearing.row.applicant || app.applicant}`}
          cheque={clearing.kind === 'guarantor'
            ? { issuer: clearing.row.guarantorName, cheque: clearing.row.chequeNumber, bank: clearing.row.bank, dueDate: clearing.row.chequeDate, amount: clearing.row.amount }
            : { issuer: clearing.row.issuerName || clearing.row.applicant || app.applicant, cheque: clearing.row.cheque, bank: clearing.row.bank, dueDate: clearing.row.dueDate, amount: clearing.row.amount }}
          onClose={() => setClearing(null)}
          onSubmit={async ({ clearedOn, depositBankId, comment }) => {
            if (clearing.kind === 'guarantor') {
              await window.QH_API.post(`/finance/applications/${app.id}/cheques/${clearing.row.id}/mark-cleared`, { clearedOn, depositBankId, comment });
            } else {
              await window.QH_API.post(`/repayments/${clearing.row.id}/mark-deposited`, { clearedOn, depositBankId, comment });
            }
            await reload();
          }}/>
      )}
    </>
  );
}

// Statement tab: numbered installment ledger driven off app.repayments.
// Server returns rows ordered by DueDate, so the row index doubles as
// the installment number. Mirrors the print column set so a Ctrl+P from
// either tab produces the same paperwork.
function StatementTab({ app, onClearCheque }) {
  const fmtBHD = window.fmtBHD || ((n) => Number(n || 0).toFixed(3));
  const fmtDate = (d) => (d || '').slice(0, 10) || '—';
  const reps = app.repayments || [];
  const banks = (window.QH_DATA?.banks || []);
  const bankName = (id) => banks.find(b => b.id === id)?.name || '—';
  const canEdit = window.can?.('repayments', 'edit');

  const totalCleared = reps.filter(r => r.status === 'cleared').reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalPending = reps.filter(r => r.status !== 'cleared').reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="card">
      <div className="card-header">
        Statement
        <span className="actions">
          <span className="muted text-xs">cleared {fmtBHD(totalCleared)} / pending {fmtBHD(totalPending)} BHD</span>
        </span>
      </div>
      <div className="card-body" style={{padding: 0}}>
        {reps.length === 0 ? (
          <div style={{padding: 14}} className="text-xs muted">No repayment cheques on this application.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{width: 80}}>Installment</th>
                <th>Cheque date</th>
                <th>Name</th>
                <th>Cheque #</th>
                <th>Drawer bank</th>
                <th className="right">Amount (BHD)</th>
                <th>Status</th>
                <th>Cleared on</th>
                <th>Deposited into</th>
                <th>Comment</th>
                <th className="no-print"/>
              </tr>
            </thead>
            <tbody>
              {reps.map((r, i) => {
                const isCleared = r.status === 'cleared';
                const isRotated = !!r.rotatedToAppId;
                return (
                  <tr key={r.id}>
                    <td className="font-mono">Installment {i + 1}</td>
                    <td className="font-mono text-xs">{fmtDate(r.dueDate)}</td>
                    <td>{r.issuerName || r.applicant || app.applicant}</td>
                    <td className="font-mono text-xs">{r.cheque || '—'}</td>
                    <td className="text-xs">{r.bank || '—'}</td>
                    <td className="num right font-mono" style={{fontWeight: 600}}>{Number(r.amount).toFixed(3)}</td>
                    <td>
                      <span className={`tag ${isCleared ? 'pos' : r.status === 'bounced' ? 'neg' : 'warn'}`} style={{textTransform: 'uppercase'}}>
                        {isCleared ? 'cleared' : (r.status || 'pending')}
                      </span>
                      {isRotated && <span className="tag" style={{marginLeft: 4, textTransform: 'none'}} title={`Rotated to ${r.rotatedToAppId}`}>rotated</span>}
                    </td>
                    <td className="font-mono text-xs">{isCleared ? fmtDate(r.clearedOn) : <span className="muted">pending</span>}</td>
                    <td className="text-xs">{r.depositBankId ? bankName(r.depositBankId) : <span className="muted">—</span>}</td>
                    <td className="text-xs muted" style={{maxWidth: 280, whiteSpace: 'pre-wrap'}}>{r.clearanceComment || '—'}</td>
                    <td className="no-print" style={{whiteSpace: 'nowrap', textAlign: 'right'}}>
                      {canEdit && !isCleared && r.status !== 'bounced' && !isRotated && (
                        <button className="btn sm" onClick={() => onClearCheque(r)} title="Record cheque clearance">
                          <I.Check s={11}/> Clear
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background: 'var(--bg-sunken)'}}>
                <td colSpan={5} className="muted text-xs uppercase">Total</td>
                <td className="num right font-mono" style={{fontWeight: 700}}>
                  {fmtBHD(reps.reduce((s, r) => s + Number(r.amount || 0), 0))}
                </td>
                <td colSpan={5}/>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// Inline row component so each payout can flip its own clear state
// without re-rendering the whole page.
function PayoutRow({ payout, canEdit, onChanged }) {
  const [busy, setBusy] = useStateFA(false);
  const fmtDate = (d) => (d || '').slice(0, 10) || '—';

  const clear = async () => {
    const dt = prompt('Cleared on (YYYY-MM-DD):', new Date().toISOString().slice(0,10));
    if (!dt) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/banks/payouts/${payout.id}/mark-cleared`, { clearedOn: dt });
      onChanged?.();
    } catch (e) { alert(e.message || 'Failed.'); }
    finally { setBusy(false); }
  };
  const unclear = async () => {
    if (!confirm('Un-clear this payout?')) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/banks/payouts/${payout.id}/unclear`, {});
      onChanged?.();
    } catch (e) { alert(e.message || 'Failed.'); }
    finally { setBusy(false); }
  };

  return (
    <tr>
      <td>{payout.bankName}</td>
      <td className="font-mono text-xs">{payout.reference || '—'}</td>
      <td className="font-mono text-xs">{fmtDate(payout.issueDate)}</td>
      <td className="text-xs">{payout.description || '—'}</td>
      <td className="num right font-mono">{Number(payout.amount).toFixed(3)}</td>
      <td><span className={`tag ${payout.status === 'cleared' ? 'pos' : 'warn'}`} style={{textTransform:'uppercase'}}>{payout.status}</span></td>
      <td className="font-mono text-xs">{fmtDate(payout.clearedOn)}</td>
      <td className="no-print" style={{whiteSpace:'nowrap'}}>
        {canEdit && payout.status !== 'cleared' && <button className="btn sm" onClick={clear} disabled={busy}><I.Check s={11}/> Clear</button>}
        {canEdit && payout.status === 'cleared' && <button className="btn sm ghost" onClick={unclear} disabled={busy}><I.X s={11}/> Un-clear</button>}
      </td>
    </tr>
  );
}

window.FinanceApplicationPage = FinanceApplicationPage;
